/**
 * Button.
 *
 * Three variants and no more. `primary` is the one action a screen wants you to take, `secondary`
 * is everything else, `danger` is destructive. A fourth variant is nearly always someone wanting a
 * different colour rather than a different meaning, and the moment two buttons on a screen both
 * look primary, neither is.
 *
 * ## Primary is orange, and that is the charte rather than a preference
 *
 * NAB's palette uses blue for identity — the wordmark, headings, navigation, links — and orange for
 * action. Every filled button in the new design is `#ff7e20` on white; the blue never appears as a
 * button fill. Reversing the two produces a screen that looks entirely reasonable and is off-brand
 * in the one way the bank is certain to notice, so it is worth saying out loud here rather than
 * leaving it implicit in a token name.
 *
 * Padding uses logical properties via Tailwind's `ps`/`pe`, so a button with an icon mirrors in
 * Arabic without a second rule.
 */

import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";

import { cx } from "./cx";

export type ButtonVariant = "primary" | "secondary" | "danger" | "ghost";
export type ButtonSize = "sm" | "md";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Rendered before the label in reading order, so it mirrors with the text. */
  icon?: ReactNode;
  /** Disables and shows a busy state. Kept separate from `disabled` so the reason is legible. */
  loading?: boolean;
}

const VARIANTS: Record<ButtonVariant, string> = {
  primary:
    "bg-accent text-onPrimary border-accent hover:bg-accentHover disabled:bg-statusNeutralSubtle disabled:text-textMuted disabled:border-borderSubtle",
  secondary:
    "bg-surfaceRaised text-primary border-borderSubtle hover:bg-primarySubtle hover:border-primaryBorder disabled:text-textMuted disabled:hover:bg-surfaceRaised",
  danger:
    "bg-surfaceRaised text-statusDanger border-statusDanger hover:bg-statusDangerSubtle disabled:bg-statusNeutralSubtle disabled:text-textMuted disabled:border-borderSubtle",
  ghost:
    "bg-transparent text-textMuted border-transparent hover:bg-primarySubtle hover:text-primary disabled:text-textMuted",
};

// Fixed heights rather than vertical padding. The design's control row puts buttons, filter chips
// and a search field side by side, and padding-derived heights drift apart by a pixel or two the
// moment any of them carries a different font size — which reads as a wobbly row, not as a bug
// anyone reports.
const SIZES: Record<ButtonSize, string> = {
  sm: "h-8 text-label-md ps-3 pe-3",
  md: "h-9 text-body-sm font-semibold ps-4 pe-4",
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = "secondary", size = "md", icon, loading, className, children, disabled, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type="button"
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cx(
        "inline-flex shrink-0 items-center justify-center gap-2 rounded-md border font-medium transition-colors",
        // A visible focus ring is not optional: this is a keyboard-heavy internal tool.
        "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary",
        "disabled:cursor-not-allowed",
        VARIANTS[variant],
        SIZES[size],
        className,
      )}
      {...rest}
    >
      {icon ? <span aria-hidden="true">{icon}</span> : null}
      {children}
    </button>
  );
});
